import * as Sentry from "@sentry/react";
import axios from "axios";
import {
  useQuery,
  QueryOptions,
  UseQueryOptions,
  DefaultError,
} from "@tanstack/react-query";
import { deltamathAPI } from "../manager/utils";
import clsx from "clsx";
import { clamp } from "lodash";
import { v4 } from "uuid";
import _ from "lodash";
import { HTMLAttributes } from "react";

export const PUBLIC_URL = process.env.PUBLIC_URL;
export const DELTAMATH_MIXPANEL_TOKEN = process.env.REACT_APP_MIXPANEL_TOKEN;
export const DELTAMATH_BREVO_CLIENT_KEY =
  process.env.REACT_APP_BREVO_CLIENT_KEY;
export const DELTAMATH_GOOGLE_OAUTH_CLIENT_ID =
  process.env.REACT_APP_GOOGLE_OAUTH_CLIENT_ID;
export const DELTAMATH_SENTRY_DSN = process.env.REACT_APP_SENTRY_DSN;

export const REACT_APP_HOMEPAGE_LINK = process.env.REACT_APP_HOMEPAGE_LINK;
export const REACT_APP_API_URL = process.env.REACT_APP_API_URL;
export const REACT_APP_SERVERLESS_API_URL =
  process.env.REACT_APP_SERVERLESS_API_URL;

export const REACT_APP_URL_PREFIX = "/app";
export const REACT_APP_ADMIN_LINK = REACT_APP_URL_PREFIX + "/admin";
export const REACT_APP_MANAGER_LINK = REACT_APP_URL_PREFIX + "/manager";
export const REACT_APP_NOTES_LINK = REACT_APP_URL_PREFIX + "/notes";
export const REACT_APP_STUDENT_LINK = REACT_APP_URL_PREFIX + "/student";
export const REACT_APP_LEARNER_LINK = REACT_APP_URL_PREFIX + "/learner";
export const REACT_APP_NEWTEACHER_LINK = REACT_APP_URL_PREFIX + "/newteacher";
export const REACT_APP_TEACHER_LINK = REACT_APP_HOMEPAGE_LINK + "/app/teacher";
export const REACT_APP_IMPERSONATION_LINK =
  REACT_APP_URL_PREFIX + "/impersonate";
export const REACT_APP_LEARNER_HOME_LINK = "/home";

export const REACT_APP_HOTJAR_ID = Number(process.env.REACT_APP_HOTJAR_ID || 0);
export const REACT_APP_GOOGLE_TAG_MANAGER_CONTAINER_ID =
  process.env.REACT_APP_GOOGLE_TAG_MANAGER_CONTAINER_ID;
export const HOTJAR_VERSION = 6;

export const PASSWORD_MIN_CHARS = 10;
export const PASSWORD_MUST_CONTAIN =
  "Your password must contain 3 of the following";
export const PASSWORD_MUST_MATCH = "Passwords must match.";

export const PasswordMessageMinCharacters: React.FC<{
  passwordValue?: string;
}> = ({ passwordValue }) => (
  <>
    Password must be at least
    <span
      className={clsx({
        "text-green-500": passwordValue && hasLength(passwordValue),
        "text-red-500": passwordValue && !hasLength(passwordValue),
      })}
    >
      {" "}
      {PASSWORD_MIN_CHARS} characters{" "}
    </span>
  </>
);

export const PasswordRequirementsList: React.FC<{ passwordValue?: string }> = ({
  passwordValue,
}) => (
  <ul className="ml-8 list-disc">
    <li
      className={clsx({
        "text-green-500": passwordValue && hasUppercase(passwordValue),
      })}
    >
      Uppercase
    </li>
    <li
      className={clsx({
        "text-green-500": passwordValue && hasLowercase(passwordValue),
      })}
    >
      Lowercase
    </li>
    <li
      className={clsx({
        "text-green-500": passwordValue && hasNumber(passwordValue),
      })}
    >
      Number
    </li>
    <li
      className={clsx({
        "text-green-500": passwordValue && hasSpecialChar(passwordValue),
      })}
    >
      Special character
    </li>
  </ul>
);

export const PasswordErrorMessages: React.FC<
  HTMLAttributes<HTMLDivElement> & { passwordValue: string }
> = ({ passwordValue, ...props }) => (
  <div {...props}>
    <p>
      <PasswordMessageMinCharacters passwordValue={passwordValue} />
      and contain at least 3 of the following:
      <br />
    </p>
    <PasswordRequirementsList passwordValue={passwordValue} />
  </div>
);

/** Renders password validation errors message only if password is not valid */
export const ConditionalPasswordErrorMessages: React.FC<{
  password: string;
}> = ({ password }) =>
  password.trim().length > 0 && !evaluatePassword(password.trim()) ? (
    <PasswordErrorMessages
      className="text-sm"
      passwordValue={password.trim()}
    />
  ) : null;

export const hasUppercase = (str: string) =>
  str.match(/[A-Z]/) !== null ? 1 : 0;
export const hasLowercase = (str: string) =>
  str.match(/[a-z]/) !== null ? 1 : 0;
export const hasSpecialChar = (str: string) =>
  str.match(/[^a-zA-Z0-9]/) !== null ? 1 : 0;
export const hasNumber = (str: string) => (str.match(/\d/) !== null ? 1 : 0);
export const hasLength = (str: string) => str.length >= PASSWORD_MIN_CHARS;

/**
 * Evaluates whether or not a password string meets SOC2 security standards.
 * @param {*} str password string to evaluate
 * @returns true or false, depending on if the string fulfills > 3/4 of the security requirements
 */
export const evaluatePassword = (str: string) => {
  const requirementsMet =
    hasUppercase(str) +
    hasLowercase(str) +
    hasSpecialChar(str) +
    hasNumber(str);
  return hasLength(str) && requirementsMet >= 3;
};

export function getErrorMessage(error: unknown) {
  if (error instanceof Error) return error.message;
  return String(error);
}

export const reportError = ({ message }: { message: string }) => {
  Sentry.captureMessage(message);
  console.error(message);
};

export const isRequestFromProd = (origin: string) =>
  origin.includes("www.deltamath.com") ? true : false;

export const isRequestFromDev = (origin: string) =>
  origin.includes("dev.deltamath.com") ? true : false;

export const isRequestFromReviewapp = (origin: string) =>
  origin &&
  (origin.includes("deltamath-manager.pages.dev") ||
    origin.includes("home-afv.pages.dev") ||
    origin.includes("deltamath-frontend.pages.dev"));

export const isRequestFromLocal = (origin: string) =>
  origin.includes("localhost");

export const getFilePath = (path: string) => {
  const separator =
    isRequestFromDev(window.location.origin) ||
    isRequestFromProd(window.location.origin)
      ? "/manager"
      : "";
  return `${REACT_APP_URL_PREFIX}${separator}${path}`;
};

export const executeQuery = async (queryObject: {
  path: string;
  params?: Record<string, unknown>;
  method?: "get" | "post" | "put" | "delete";
  requestBody?: Record<string, unknown>;
  additionalHeaders?: Record<string, unknown>;
  customerServiceHeader?: boolean;
  responseType?: string;
  returnHeaders?: boolean;
}) => {
  const {
    path,
    params,
    method,
    requestBody,
    additionalHeaders,
    customerServiceHeader,
    responseType,
    returnHeaders,
  } = queryObject;
  const user = JSON.parse(localStorage.getItem("user") || "{}");
  const userId = user._id || new Date().getTime();
  let token: string | null = null;
  if (typeof customerServiceHeader !== "undefined" && customerServiceHeader) {
    token = localStorage.getItem("customer_service_token");
  }
  const axiosConfig: Record<string, unknown> = {
    url: deltamathAPI() + path,
    // withCredentials: true,
    headers: {
      "Content-Type": "application/json",
      ...(token &&
        !axios.defaults.headers.common["Authorization"] && {
          Authorization: `Bearer ${token}`,
        }),
      ...additionalHeaders,
    },
  };
  if (typeof params !== "undefined") {
    // if we have a get request we want to add the userId to the params to stop browser cache from potentially serving old data the contains another user's JWT or cookie
    if (method === "get" || method === undefined) {
      if (params["rId"]) {
        const guid = v4();
        params[guid.substring(0, 8)] = userId;
      } else {
        params["rId"] = userId;
      }
    }
    axiosConfig.params = params;
  } else {
    if (method === "get" || method === undefined) {
      axiosConfig.params = { rId: userId };
    }
  }
  if (typeof method !== "undefined") {
    axiosConfig.method = method;
  }
  if (typeof requestBody !== "undefined" && method !== "get") {
    axiosConfig.data = requestBody;
  }
  if (typeof responseType !== "undefined") {
    axiosConfig.responseType = responseType;
  }
  try {
    const request = await axios.request(axiosConfig);
    if (typeof returnHeaders === "boolean" && returnHeaders) {
      return { responseData: request.data, responseHeaders: request.headers };
    }
    return request.data;
  } catch (error: any) {
    reportError({ message: getErrorMessage(error) }); //log error to console
    throw error.response.data; // return { error } from useQuery()
  }
};

// I know that this is bad practice, but until we  have a good way to share types between the API and frontend, going to use this
// eslint-disable-next-line @typescript-eslint/no-explicit-any
export function useDMQuery<T = any>(queryObject: {
  path: string;
  params?: Record<string, unknown>;
  cacheKey?: string | string[];
  method?: "get" | "post" | "put" | "delete";
  requestBody?: Record<string, unknown>;
  queryOptions?: Omit<
    UseQueryOptions<T, DefaultError, T>,
    "queryKey" | "queryFn" | "select"
  >;
  additionalHeaders?:
    | { "Content-Type": "multipart/form-data" }
    | Record<string, unknown>;
  customerServiceHeader?: boolean;
  modifyData?: (data: any) => any;
}) {
  const {
    path,
    params,
    cacheKey,
    method,
    requestBody,
    queryOptions,
    additionalHeaders,
    customerServiceHeader,
    modifyData,
  } = queryObject;
  if (!queryObject.path) {
    throw new Error("useDMQuery requires a path");
  }
  const queryKey =
    typeof cacheKey === "string" ? [cacheKey] : cacheKey || [path];
  return useQuery<T>({
    queryKey: queryKey,
    queryFn: async () =>
      executeQuery({
        path,
        params: _.cloneDeep(params), //using deep clone b/c sometimes the params may be a piece of state in the component using this hook and we are mutating this now in executeQuery to add userId to prevent data from being shared between users
        method,
        requestBody,
        additionalHeaders,
        customerServiceHeader,
      }),
    ...queryOptions,
    retry: false,
    select: modifyData,
  });
}

export const formatNumber = (num: number) => {
  return num === Math.round(num)
    ? num.toLocaleString()
    : num.toFixed(2).replace(/\d(?=(\d{3})+\.)/g, "$&,");
};

export function timeConverter(UNIX_timestamp: number) {
  if (UNIX_timestamp) {
    const a = new Date(UNIX_timestamp * 1000);
    return new Intl.DateTimeFormat("en-US").format(a);
  }
  return "";
}

export function isTokenExpired(token: string) {
  const base64Url = token.split(".")[1];
  const base64 = base64Url.replace(/-/g, "+").replace(/_/g, "/");
  const jsonPayload = decodeURIComponent(
    atob(base64)
      .split("")
      .map(function (c) {
        return "%" + ("00" + c.charCodeAt(0).toString(16)).slice(-2);
      })
      .join("")
  );

  const { exp } = JSON.parse(jsonPayload);
  const expired = Date.now() >= exp * 1000;
  return expired;
}

/**
 * Throw an error if a section of code is impossible to reach.
 * Use this to highlight unhandled cases in unions
 */
// eslint-disable-next-line @typescript-eslint/no-unused-vars
export function unreachableCase(x: never): never {
  throw new Error(
    "Unreachable section of code was reached, or unhandled case introduced"
  );
}

/**
 * Given a learner/student's `ans` determine which choice they selected in
 * a multiple choice question. For multiple choice questions, `ans` is
 * stored as an array with the index of the selected choice, e.g. `['2']`
 */
export function getSelectedChoice(
  ans: any,
  choices: string[]
): string | undefined {
  const answerIndex = Array.isArray(ans) && ans[0];
  return choices ? choices[parseInt(answerIndex, 10)] : undefined;
}

/**
 * Clamp a number between -1 and 1, for use in sort functions
 * @example clampForSort(valueA - valueB)
 */
export function clampForSort(num: number): number {
  return clamp(num, -1, 1);
}

// Using this to fix issue w/ logo reloading and flashing on re-renders
export const deltamathLogo =
  "data:image/png;base64,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";

export function centerVerticallyInParent(parent: Element, child: Element) {
  const parentRect = parent.getBoundingClientRect();
  const childRect = child.getBoundingClientRect();

  parent.scrollTop += childRect.top - parentRect.top - parent.clientHeight / 2;
}
